import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'
import { Zap, Clock, Truck, Stethoscope, Microscope, Pill, Smartphone, RefreshCw } from 'lucide-react'

// Native app → compact, app-like layout (web look unchanged)
let _Cap = null
try { _Cap = require('@capacitor/core').Capacitor } catch (e) {}
const IS_NATIVE = !!(_Cap && _Cap.isNativePlatform && _Cap.isNativePlatform())

const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42',
  g500:'#3D8A57', g400:'#5AAE73', g300:'#85C99A', g100:'#E8F5EE', g50:'#F2FAF5',
  white:'#FFFFFF', text:'#1A2820', muted:'#5A7065', border:'#C8DDD2', error:'#C0392B',
}

const SERVICES = [
  'Skin Consultation','Hair Consultation','Acne Treatment','Hair Loss Treatment',
  'Pigmentation & Melasma','Anti-Ageing Treatment','Eczema / Psoriasis',
  'Chemical Peel','Laser Toning','Mole / Lesion Check','Prescription Skincare',
  'Follow-up Consultation','Other',
]

const TIMES = []
for (let h = 9; h <= 20; h++) {
  for (let m = 0; m < 60; m += 30) {
    if (h === 20 && m > 0) break
    const period = h < 12 ? 'AM' : 'PM'
    const h12 = h > 12 ? h - 12 : h
    TIMES.push(`${h12}:${m === 0 ? '00' : m} ${period}`)
  }
}

const getTodayStr = () => new Date().toISOString().split('T')[0]

const inp = (focused, hasErr) => ({
  width:'100%', padding:'.85rem 1.1rem',
  border:`1.5px solid ${hasErr ? C.error : focused ? C.g500 : C.border}`,
  borderRadius:10, fontSize:'.875rem', outline:'none',
  fontFamily:"'DM Sans',sans-serif", color:C.text, background:'white',
  boxSizing:'border-box', transition:'border-color .2s',
})

function SectionHead({ icon, title, sub }) {
  return (
    <div style={{ padding:'.85rem 2rem', background:C.g50, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10 }}>
      <span style={{ fontSize:'1rem' }}>{icon}</span>
      <span style={{ fontWeight:600, fontSize:'.9rem', color:C.g800 }}>{title}</span>
      {sub && <span style={{ fontSize:'.75rem', color:C.muted, marginLeft:4 }}>({sub})</span>}
    </div>
  )
}

function Field({ label, error, children, col2 }) {
  return (
    <div style={{ gridColumn: IS_NATIVE ? 'auto' : (col2 ? 'span 1' : 'span 2') }}>
      <label style={{ fontSize:'.72rem', fontWeight:600, color:C.muted, letterSpacing:'.07em', textTransform:'uppercase', display:'block', marginBottom:'.4rem' }}>{label}</label>
      {children}
      {error && <p style={{ fontSize:'.72rem', color:C.error, marginTop:'.3rem' }}>⚠ {error}</p>}
    </div>
  )
}

export default function BookAppointment() {
  const auth = useAuth()
  const navigate = useNavigate()

  // Real dermatologist team (multi-doctor); appointment is auto-assigned by us
  const [docs, setDocs] = React.useState([])
  React.useEffect(() => {
    fetch(`${API_BASE}/api/doctors`).then(r => r.ok ? r.json() : { doctors: [] })
      .then(j => setDocs(Array.isArray(j?.doctors) ? j.doctors : [])).catch(() => {})
  }, [])

  React.useEffect(() => {
    if (auth && auth.user === null) {
      navigate('/login?redirect=/book-appointment')
    }
  }, [auth, navigate])

  const [focused, setFocused] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [success, setSuccess] = React.useState(false)
  const [errors, setErrors] = React.useState({})
  const [photos, setPhotos] = React.useState([])        // { file, preview }
  const [dragOver, setDragOver] = React.useState(false)
  const fileInputRef = React.useRef(null)
  const cameraInputRef = React.useRef(null)   // capture="environment" → opens the camera directly on mobile

  // Camera photos are often 3–8 MB — far too big to upload reliably on a phone
  // connection. Downscale to max 1600px + JPEG ~0.82 before upload (usually <400 KB).
  const downscaleImage = (file) => new Promise((resolve) => {
    if (file.size < 900 * 1024) { resolve(file); return } // already small enough
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const MAX = 1600
      const scale = Math.min(1, MAX / Math.max(img.width, img.height))
      const w = Math.max(1, Math.round(img.width * scale))
      const h = Math.max(1, Math.round(img.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], (file.name || 'photo') + '.jpg', { type: 'image/jpeg' }) : file),
        'image/jpeg', 0.82
      )
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
    img.src = url
  })

  const addPhotos = async (files) => {
    const valid = Array.from(files).filter(f => f.type.startsWith('image/')).slice(0, 5 - photos.length)
    for (const file of valid) {
      const small = await downscaleImage(file)
      const reader = new FileReader()
      reader.onload = e => setPhotos(prev => [...prev, { file: small, preview: e.target.result }])
      reader.readAsDataURL(small)
    }
  }

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx))

  // OTP/phone users carry their phone in the token's email claim — route it to the phone field
  const _isPhoneLike = s => { const t = String(s || '').trim(); return !!t && !t.includes('@') && (t.match(/\d/g) || []).length >= 7 }
  const _rawId = auth?.user?.email || ''
  const _idIsPhone = _isPhoneLike(_rawId)
  // Pre-fill the service from the concern the patient tapped (e.g. /book-appointment?concern=Acne%20%26%20Scars)
  const _params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '')
  const _CONCERN_SERVICE = {
    'acne & scars': 'Acne Treatment', 'acne': 'Acne Treatment', 'hair loss': 'Hair Loss Treatment', 'hair': 'Hair Loss Treatment',
    'pigmentation': 'Pigmentation & Melasma', 'eczema': 'Eczema / Psoriasis', 'psoriasis': 'Eczema / Psoriasis',
    'anti-aging': 'Anti-Ageing Treatment', 'anti-ageing': 'Anti-Ageing Treatment',
  }
  const _concern = (_params.get('concern') || '').toLowerCase().trim()
  const _initService = _CONCERN_SERVICE[_concern] || (SERVICES.includes(_params.get('service')) ? _params.get('service') : '')

  const [form, setForm] = React.useState({
    name: auth?.user?.name || '',
    email: _idIsPhone ? '' : _rawId,
    phone: _idIsPhone ? _rawId : '',
    date: '',
    timeSlot: '',
    service: _initService,
    notes: '',
    mode: 'online',
    locality: '',
  })

  const handle = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }))
    setErrors(er => ({ ...er, [e.target.name]: '' }))
  }

  const validate = () => {
    const errs = {}
    if (!form.name.trim())  errs.name     = 'Name is required'
    if (!form.email.trim()) errs.email    = 'Email is required'
    if (!form.phone.trim()) errs.phone    = 'Phone is required'
    if (!form.date)         errs.date     = 'Please select a date'
    if (!form.timeSlot)     errs.timeSlot = 'Please select a time'
    if (!form.service)      errs.service  = 'Please select a service'
    console.log('[BookAppointment] form state on submit:', form)
    console.log('[BookAppointment] validation errors:', errs)
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const submit = async (e) => {
    e.preventDefault()
    console.log('[BookAppointment] Submit clicked')

    if (!validate()) {
      console.warn('[BookAppointment] Validation failed — stopping here')
      return
    }

    setLoading(true)
    try {
      const url = `${API_BASE}/api/appointments`

      // Use FormData so photos can be uploaded
      const fd = new FormData()
      Object.entries({ ...form, message: form.notes }).forEach(([k, v]) => fd.append(k, v))
      photos.forEach((p, i) => fd.append(`photo_${i}`, p.file))

      // Not logged in → send to login and come back to booking
      if (!auth?.user?.token) {
        navigate('/login?redirect=/book-appointment')
        return
      }

      // fetchWithAuth pulls the LIVE token from context (survives refresh) and
      // retries once on 401. Do NOT set Content-Type — the browser adds the
      // multipart boundary for FormData. Abort after 60s so a stalled upload
      // surfaces an error instead of spinning forever.
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 60000)
      let res
      try {
        res = await auth.fetchWithAuth(url, { method: 'POST', body: fd, signal: controller.signal })
      } finally {
        clearTimeout(timer)
      }

      console.log("[BookAppointment] Response status:", res.status)

      if (res.status === 401) {
        console.warn("[BookAppointment] 401 Unauthorized — redirecting to login")
        navigate("/login?redirect=/book-appointment")
        return
      }
      if (!res.ok) {
        const errText = await res.text()
        console.error("[BookAppointment] Error body:", errText)
        let data = {}
        try { data = JSON.parse(errText) } catch {}
        const e = new Error(data.message || `Something went wrong (error ${res.status}). Please try again.`)
        e.slotTaken = res.status === 409 && !!data.slotTaken
        e.free = Array.isArray(data.free) ? data.free : []
        throw e
      }

      setSuccess(true)
    } catch (err) {
      console.error('[BookAppointment] Caught error:', err)
      if (err.name === 'AbortError') {
        setErrors(er => ({ ...er, submit: 'Upload timed out — please check your connection and try again (fewer/smaller photos help).', submitInfo: false }))
      } else if (err.slotTaken) {
        const free = err.free && err.free.length ? ` Try: ${err.free.slice(0, 6).join(', ')}.` : ''
        setErrors(er => ({ ...er, submit: `This time slot was just booked by someone else. Please pick another time.${free}`, submitInfo: true }))
      } else {
        setErrors(er => ({ ...er, submit: err.message, submitInfo: false }))
      }
    } finally {
      setLoading(false)
    }
  }

  // ── SUCCESS ──
  if (success) return (
    <div style={{ minHeight:'100vh', background:C.g50, display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}>
      <div style={{ background:'white', borderRadius:24, padding:'3.5rem', maxWidth:520, width:'100%', textAlign:'center', boxShadow:'0 16px 56px rgba(13,34,24,.1)', border:`1px solid ${C.border}` }}>
        <div style={{ width:80, height:80, borderRadius:'50%', background:C.g100, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.5rem', margin:'0 auto 1.5rem' }}>✅</div>
        <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'2rem', color:C.g900, marginBottom:'.75rem' }}>Appointment Booked!</h2>
        <p style={{ fontSize:'.9rem', color:C.muted, lineHeight:1.8, marginBottom:'2rem' }}>
          Your {form.mode==='online'?'online consultation':'clinic visit'} is scheduled for{' '}
          <strong style={{ color:C.g700 }}>{form.date}</strong> at <strong style={{ color:C.g700 }}>{form.timeSlot}</strong>.<br/>
          Confirmation will be sent to <strong style={{ color:C.g700 }}>{form.email}</strong>.
        </p>
        <div style={{ background:C.g50, borderRadius:14, padding:'1.25rem', marginBottom:'2rem', textAlign:'left' }}>
          {[['Patient',form.name],['Service',form.service],['Mode','Online — ₹500']].map(([l,v])=>(
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'.6rem 0', borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:'.75rem', fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.06em' }}>{l}</span>
              <span style={{ fontSize:'.875rem', color:C.text, fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:'1rem', justifyContent:'center', flexWrap:'wrap' }}>
          <button onClick={() => navigate('/patient-dashboard')} style={{ padding:'.8rem 1.75rem', borderRadius:9, background:C.g700, color:'white', border:'none', fontSize:'.875rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", boxShadow:`0 4px 12px rgba(35,82,51,.25)` }}>View Dashboard</button>
          <button onClick={() => navigate('/')} style={{ padding:'.8rem 1.75rem', borderRadius:9, background:'white', color:C.g700, border:`1px solid ${C.border}`, fontSize:'.875rem', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Back to Home</button>
        </div>
      </div>
    </div>
  )

  // ── MAIN ──
  return (
    <div style={{ background:'white', minHeight:'100vh' }}>

      {/* HERO (compact bar in the app, full hero on web) */}
      {IS_NATIVE ? (
        <div style={{ background:C.headerGrad, padding:'3.2rem 1.3rem 1.2rem', color:'white' }}>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.35rem' }}>Book Consultation</div>
          <div style={{ fontSize:'.78rem', color:'rgba(255,255,255,.65)', marginTop:4 }}>Online video consult · ₹500 · 9 AM – 8 PM, 7 days</div>
        </div>
      ) : (
      <div style={{ background:`linear-gradient(145deg,${C.g900},${C.g800},${C.g700})`, padding:'2.2rem 2rem 2rem', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(circle,rgba(255,255,255,.025) 1px,transparent 1px)`, backgroundSize:'28px 28px', pointerEvents:'none' }} />
        <div style={{ position:'absolute', right:'-5%', top:'-20%', width:400, height:400, borderRadius:'50%', background:`radial-gradient(circle,rgba(93,174,115,.12) 0%,transparent 65%)`, pointerEvents:'none' }} />
        <div style={{ maxWidth:1100, margin:'0 auto', position:'relative', zIndex:1, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'2rem' }}>
          <div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', borderRadius:999, padding:'.3rem .9rem', marginBottom:'.8rem' }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:C.g300, display:'inline-block' }} />
              <span style={{ fontSize:'.7rem', color:'rgba(255,255,255,.7)', letterSpacing:'.14em', textTransform:'uppercase' }}>Expert Dermatologists</span>
            </div>
            <h1 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.6rem,3vw,2.2rem)', color:'white', lineHeight:1.1, marginBottom:'.6rem' }}>
              Book Your <em style={{ color:C.g300, fontStyle:'italic' }}>Appointment</em>
            </h1>
            <p style={{ fontSize:'.88rem', color:'rgba(255,255,255,.6)', lineHeight:1.6, maxWidth:460, fontWeight:300 }}>
              Consult online via Google Meet for ₹500 — from anywhere in India.<br/>Available 7 days a week · 9 AM – 8 PM.
            </p>
          </div>
          <div style={{ display:'flex', gap:'2rem' }}>
            {[[Zap,'₹500','Online'],[Clock,'9AM–8PM','Hours'],[Truck,'Free','Delivery'],[Stethoscope,'MD','Doctors']].map(([Ic,val,lbl])=>(
              <div key={lbl} style={{ textAlign:'center' }}>
                <div style={{ display:'flex', justifyContent:'center', marginBottom:'.35rem' }}><Ic size={20} strokeWidth={1.75} color={C.g300} /></div>
                <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', color:C.g300 }}>{val}</div>
                <div style={{ fontSize:'.62rem', color:'rgba(255,255,255,.4)', textTransform:'uppercase', letterSpacing:'.08em' }}>{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* FORM AREA */}
      <div style={{ maxWidth:1100, margin:'0 auto', padding:IS_NATIVE?'1.2rem 1rem 2rem':'3rem 2rem' }}>
        {/* Mobile-web: stack the 2-col layout + paired fields (the app already stacks via IS_NATIVE) */}
        <style>{`@media (max-width:820px){ .ba-grid{grid-template-columns:1fr!important} .ba-2col{grid-template-columns:1fr!important} }`}</style>
        <div className="ba-grid" style={{ display:'grid', gridTemplateColumns:IS_NATIVE?'1fr':'1fr 340px', gap:'2rem', alignItems:'start' }}>

          {/* ── FORM ── */}
          <form onSubmit={submit}>
            <div style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:20, overflow:'hidden', boxShadow:'0 4px 24px rgba(13,34,24,.06)' }}>

              {/* Personal Details */}
              <SectionHead icon="👤" title="Your Details" />
              <div className="ba-2col" style={{ padding:IS_NATIVE?'1.1rem':'1.75rem 2rem', display:'grid', gridTemplateColumns:IS_NATIVE?'1fr':'1fr 1fr', gap:'1rem' }}>
                <Field label="Full Name *" error={errors.name}>
                  <input name="name" value={form.name} onChange={handle} placeholder="Priya Sharma"
                    onFocus={()=>setFocused('name')} onBlur={()=>setFocused(null)}
                    style={inp(focused==='name', errors.name)} />
                </Field>
                <Field label="Phone Number *" error={errors.phone} col2>
                  <input name="phone" value={form.phone} onChange={handle} placeholder="+91 98765 43210"
                    onFocus={()=>setFocused('phone')} onBlur={()=>setFocused(null)}
                    style={inp(focused==='phone', errors.phone)} />
                </Field>
                <Field label="Email Address *" error={errors.email} col2>
                  <input name="email" type="email" value={form.email} onChange={handle} placeholder="priya@example.com"
                    onFocus={()=>setFocused('email')} onBlur={()=>setFocused(null)}
                    style={inp(focused==='email', errors.email)} />
                </Field>
              </div>

              {/* Mode */}
              <SectionHead icon="💻" title="Consultation Mode" />
              <div style={{ padding:'1.75rem 2rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, background:C.g50, border:`1.5px solid ${C.border}`, borderRadius:12, padding:'.9rem 1.1rem', marginBottom:'1rem' }}>
                  <span style={{ fontSize:'1.4rem' }}>💻</span>
                  <div>
                    <div style={{ fontSize:'.88rem', fontWeight:700, color:C.g900 }}>Online — ₹500 (Google Meet)</div>
                    <div style={{ fontSize:'.75rem', color:C.muted }}>All consultations are online — consult from anywhere in India.</div>
                  </div>
                </div>
                <div style={{ background:C.g100, border:`1px solid ${C.border}`, borderRadius:10, padding:'.75rem 1rem', fontSize:'.82rem', color:C.g700 }}>
                  ⚡ You'll receive a Google Meet link 30 minutes before your appointment.
                </div>
              </div>

              {/* Schedule */}
              <SectionHead icon="📅" title="Schedule" />
              <div className="ba-2col" style={{ padding:IS_NATIVE?'1.1rem':'1.75rem 2rem', display:'grid', gridTemplateColumns:IS_NATIVE?'1fr':'1fr 1fr', gap:'1rem' }}>
                <Field label="Preferred Date *" error={errors.date} col2>
                  <input name="date" type="date" value={form.date} onChange={handle}
                    min={getTodayStr()}
                    onFocus={()=>setFocused('date')} onBlur={()=>setFocused(null)}
                    style={inp(focused==='date', errors.date)} />
                </Field>
                <Field label="Preferred Time *" error={errors.timeSlot} col2>
                  <select name="timeSlot" value={form.timeSlot} onChange={handle}
                    onFocus={()=>setFocused('timeSlot')} onBlur={()=>setFocused(null)}
                    style={{...inp(focused==='timeSlot', errors.timeSlot), cursor:'pointer'}}>
                    <option value="">Select a time…</option>
                    <optgroup label="Morning">
                      {TIMES.filter(t=>t.includes('AM')).map(t=><option key={t} value={t}>{t}</option>)}
                    </optgroup>
                    <optgroup label="Afternoon / Evening">
                      {TIMES.filter(t=>t.includes('PM')).map(t=><option key={t} value={t}>{t}</option>)}
                    </optgroup>
                  </select>
                </Field>
                <Field label="Service / Concern *" error={errors.service}>
                  <select name="service" value={form.service} onChange={handle}
                    onFocus={()=>setFocused('service')} onBlur={()=>setFocused(null)}
                    style={{...inp(focused==='service', errors.service), cursor:'pointer'}}>
                    <option value="">Select a service…</option>
                    {SERVICES.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>

              {/* Photos */}
              <SectionHead icon="📷" title="Skin / Hair Photos" sub="optional, up to 5 images" />
              <div style={{ padding:'1.75rem 2rem' }}>
                {/* Drop zone */}
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); addPhotos(e.dataTransfer.files) }}
                  style={{ border:`2px dashed ${dragOver ? C.g500 : C.border}`, borderRadius:12, padding:'1.5rem', textAlign:'center', background: dragOver ? C.g50 : 'white', transition:'all .2s' }}>
                  {/* Hidden inputs: camera (capture) + gallery/browse (multiple) */}
                  <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display:'none' }}
                    onChange={e => { addPhotos(e.target.files); e.target.value = '' }} />
                  <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display:'none' }}
                    onChange={e => { addPhotos(e.target.files); e.target.value = '' }} />
                  <div style={{ fontSize:'2rem', marginBottom:'.5rem' }}>📷</div>
                  <div style={{ fontSize:'.85rem', fontWeight:600, color:C.g700, marginBottom:'.75rem' }}>
                    Add photos of your skin / hair
                  </div>
                  <div style={{ display:'flex', gap:'.6rem', justifyContent:'center', flexWrap:'wrap', marginBottom:'.6rem' }}>
                    <button type="button" onClick={() => cameraInputRef.current?.click()}
                      style={{ display:'flex', alignItems:'center', gap:6, background:C.g700, color:'#fff', border:'none', borderRadius:10, padding:'.6rem 1.1rem', fontSize:'.82rem', fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
                      📷 Take Photo
                    </button>
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                      style={{ display:'flex', alignItems:'center', gap:6, background:'#fff', color:C.g700, border:`1.5px solid ${C.g300}`, borderRadius:10, padding:'.6rem 1.1rem', fontSize:'.82rem', fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
                      🖼️ {IS_NATIVE ? 'Gallery' : 'Browse'}
                    </button>
                  </div>
                  <div style={{ fontSize:'.72rem', color:C.muted }}>
                    JPG, PNG, WEBP · Max 5 photos · Helps the doctor assess your condition
                  </div>
                </div>

                {/* Previews */}
                {photos.length > 0 && (
                  <div style={{ display:'flex', gap:'.75rem', flexWrap:'wrap', marginTop:'1rem' }}>
                    {photos.map((p, i) => (
                      <div key={i} style={{ position:'relative', width:90, height:90, borderRadius:10, overflow:'hidden', border:`1.5px solid ${C.border}`, flexShrink:0 }}>
                        <img src={p.preview} alt={`photo-${i}`} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        <button onClick={() => removePhoto(i)}
                          style={{ position:'absolute', top:4, right:4, width:22, height:22, borderRadius:'50%', background:'rgba(0,0,0,.6)', color:'white', border:'none', cursor:'pointer', fontSize:'.7rem', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}>
                          ✕
                        </button>
                      </div>
                    ))}
                    {photos.length < 5 && (
                      <div onClick={() => fileInputRef.current?.click()}
                        style={{ width:90, height:90, borderRadius:10, border:`2px dashed ${C.border}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:C.muted, fontSize:'1.5rem', flexShrink:0 }}>
                        +
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              <SectionHead icon="📍" title="Your Area / Locality" sub="helps us estimate travel time" />
              <div style={{ padding:'1.75rem 2rem 0' }}>
                <input name="locality" value={form.locality} onChange={handle}
                  placeholder="e.g. Koramangala, Indiranagar, HSR Layout…"
                  onFocus={()=>setFocused('locality')} onBlur={()=>setFocused(null)}
                  style={inp(focused==='locality', false)} />
              </div>

              <SectionHead icon="📝" title="Additional Notes" sub="optional" />
              <div style={{ padding:'1.75rem 2rem' }}>
                <textarea name="notes" value={form.notes} onChange={handle} rows={4}
                  placeholder="Describe your concern, how long you've had it, any medications or products you currently use…"
                  onFocus={()=>setFocused('notes')} onBlur={()=>setFocused(null)}
                  style={{...inp(focused==='notes', false), resize:'vertical'}} />
              </div>

              {/* Error / info banner */}
              {errors.submit && (
                <div style={{ margin:'0 2rem', padding:'1rem 1.25rem', background: errors.submitInfo ? '#FFFBEB' : '#FEF2F2', border:`1.5px solid ${errors.submitInfo ? '#FDE68A' : '#FECACA'}`, borderRadius:12, display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background: errors.submitInfo ? '#FEF3C7' : '#FEE2E2', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', flexShrink:0 }}>{errors.submitInfo ? '📅' : '⚠️'}</div>
                  <div>
                    <div style={{ fontSize:'.875rem', fontWeight:600, color: errors.submitInfo ? '#92400E' : '#991B1B', marginBottom:'.15rem' }}>{errors.submitInfo ? 'Slot Unavailable' : 'Booking Failed'}</div>
                    <div style={{ fontSize:'.8rem', color: errors.submitInfo ? '#B45309' : '#B91C1C' }}>{errors.submit}</div>
                  </div>
                </div>
              )}

              {/* Submit bar */}
              <div style={{ padding:'1.5rem 2rem', borderTop:`1px solid ${C.border}`, background:C.g50, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'1rem' }}>
                <div style={{ fontSize:'.82rem', color:C.muted }}>All fields marked * are required</div>
                <button type="submit" disabled={loading}
                  style={{ padding:'.9rem 2.5rem', borderRadius:10, background:loading?C.g400:C.g700, color:'white', border:'none', fontSize:'.95rem', fontWeight:600, cursor:loading?'not-allowed':'pointer', fontFamily:"'DM Sans',sans-serif", letterSpacing:'.04em', boxShadow:`0 4px 16px rgba(35,82,51,.25)`, display:'flex', alignItems:'center', gap:9, transition:'all .2s' }}
                  onMouseEnter={e=>{if(!loading)e.currentTarget.style.background=C.g800}}
                  onMouseLeave={e=>{if(!loading)e.currentTarget.style.background=C.g700}}>
                  {loading
                    ? <><span style={{ width:16, height:16, borderRadius:'50%', border:'2px solid rgba(255,255,255,.3)', borderTopColor:'white', display:'inline-block', animation:'spin .8s linear infinite' }} />Booking…</>
                    : '✓ Book Appointment'}
                </button>
              </div>
            </div>
          </form>

          {/* ── SIDEBAR ── */}
          <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            <div style={{ background:C.g700, borderRadius:18, padding:'1.75rem' }}>
              <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', color:'white', marginBottom:'1.1rem' }}>Why consult with us?</div>
              {[[Microscope,'Evidence-based treatment plans'],[Pill,'Prescription skincare delivered free'],[Smartphone,'Online or in-clinic — your choice'],[RefreshCw,'Structured follow-up care']].map(([Ic,text])=>(
                <div key={text} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:'.75rem' }}>
                  <span style={{ width:30, height:30, borderRadius:8, background:'rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Ic size={16} strokeWidth={1.75} color="#fff" /></span>
                  <span style={{ fontSize:'.8rem', color:'rgba(255,255,255,.75)', lineHeight:1.5 }}>{text}</span>
                </div>
              ))}
            </div>
            <div style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:18, padding:'1.75rem' }}>
              <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.05rem', color:C.g900, marginBottom:'1rem' }}>🕗 Clinic Hours</div>
              {[['Mon – Sun','10:00 AM – 6:00 PM',true]].map(([d,t,open])=>(
                <div key={d} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'.6rem 0', borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:'.83rem', color:C.text, fontWeight:500 }}>{d}</span>
                  <span style={{ fontSize:'.78rem', color:open?C.g600:C.muted, background:open?C.g100:'#f5f5f5', padding:'.2rem .75rem', borderRadius:999, fontWeight:500 }}>{t}</span>
                </div>
              ))}
            </div>
            <div style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:18, padding:'1.75rem' }}>
              <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.05rem', color:C.g900, marginBottom:'1rem' }}>👨‍⚕️ Our Dermatologists</div>
              {docs.length > 0 ? docs.map((d, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:'.85rem' }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background:C.g700, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Serif Display',serif", fontSize:'1rem', flexShrink:0 }}>{(d.name || 'D')[0]}</div>
                  <div>
                    <div style={{ fontSize:'.83rem', fontWeight:600, color:C.g900 }}>{d.name}</div>
                    <div style={{ fontSize:'.72rem', color:C.muted }}>{d.specialization || d.qualification || 'Dermatologist'}</div>
                  </div>
                </div>
              )) : (
                <div style={{ fontSize:'.8rem', color:C.muted, lineHeight:1.6 }}>You'll be seen by a <b>verified, MD-qualified dermatologist</b> from our team. We assign the right doctor for your concern.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
